<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ContactsTableRow from 'dashboard/components-next/Contacts/Pages/ContactsTableRow.vue';

const props = defineProps({
  contacts: { type: Array, required: true },
  columns: { type: Array, required: true },
  selectedContactIds: { type: Array, default: () => [] },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits([
  'toggleContact',
  'toggleAll',
  'update:sort',
  'showContact',
]);

const { t } = useI18n();

const selectedIdsSet = computed(() => new Set(props.selectedContactIds || []));

const allSelected = computed(
  () =>
    props.contacts.length > 0 &&
    props.contacts.every(contact => selectedIdsSet.value.has(contact.id))
);

const someSelected = computed(
  () => props.selectedContactIds.length > 0 && !allSelected.value
);

const columnLabel = column =>
  column.isCustom ? column.label : t(column.labelKey);

const ariaSort = column => {
  if (!column.sortKey || column.sortKey !== props.activeSort) return 'none';
  return props.activeOrdering === '-' ? 'descending' : 'ascending';
};

const sortIndicator = column => {
  if (!column.sortKey || column.sortKey !== props.activeSort) return '';
  return props.activeOrdering === '-'
    ? 'i-lucide-arrow-down'
    : 'i-lucide-arrow-up';
};

const handleSort = column => {
  if (!column.sortKey) return;
  const isActive = column.sortKey === props.activeSort;
  // Toggle direction when re-sorting the same column, else default to ascending.
  const order = isActive && props.activeOrdering === '' ? '-' : '';
  emit('update:sort', { sort: column.sortKey, order });
};

const handleToggleAll = event => {
  emit('toggleAll', event.target.checked);
};

const isSelected = id => selectedIdsSet.value.has(id);
</script>

<template>
  <div class="w-full overflow-x-auto">
    <table class="w-full text-left border-collapse">
      <thead
        class="sticky top-0 z-10 bg-n-background border-b border-n-weak"
      >
        <tr>
          <th scope="col" class="w-10 px-3 py-2">
            <Checkbox
              :model-value="allSelected"
              :indeterminate="someSelected"
              :aria-label="t('CONTACTS_LAYOUT.TABLE.SELECT_ALL')"
              @change="handleToggleAll"
            />
          </th>
          <th
            v-for="column in columns"
            :key="column.key"
            scope="col"
            :aria-sort="ariaSort(column)"
            class="px-3 py-2 text-xs font-medium tracking-wide uppercase text-n-slate-10 whitespace-nowrap"
          >
            <button
              v-if="column.sortKey"
              type="button"
              class="inline-flex items-center gap-1 hover:text-n-slate-12"
              @click="handleSort(column)"
            >
              <span class="truncate">{{ columnLabel(column) }}</span>
              <span
                v-if="sortIndicator(column)"
                :class="sortIndicator(column)"
                class="size-3.5 shrink-0"
              />
            </button>
            <span v-else class="truncate">{{ columnLabel(column) }}</span>
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak">
        <ContactsTableRow
          v-for="contact in contacts"
          :key="contact.id"
          :contact="contact"
          :columns="columns"
          :is-selected="isSelected(contact.id)"
          @toggle-select="value => emit('toggleContact', { id: contact.id, value })"
          @show-contact="emit('showContact', contact.id)"
        />
      </tbody>
    </table>
  </div>
</template>
